import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/* =========================================
   STORAGE
========================================= */

const STORAGE_KEY = 'freeGiftState';

window.getGiftState = function () {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

window.setGiftState = function (next) {
  const prev = getGiftState();
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prev, ...next }));
}

window.resetGiftState = function () {
  localStorage.removeItem(STORAGE_KEY);
}

/* =========================================
   CART FETCH
========================================= */

window.getCartFresh = async function () {
  try {
    const res = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!res.ok) throw new Error('Cart fetch failed');
    return await res.json();
  } catch (e) {
    console.error('[FreeGift] Cart fetch error:', e);
    return null;
  }
}

/* =========================================
   HELPERS
========================================= */
window.closePopup = function () {
  document.querySelector('#free-gift-dialog dialog')?.close();
}

document.addEventListener('DOMContentLoaded', () => {

  const dialog = document.querySelector('#free-gift-dialog');

  if (!dialog) return;

  dialog.addEventListener('close', () => {

    const state = getGiftState();

    if (!state.paidProductAdded) {
      setGiftState({ firstPopupClosed: true });
    } else {
      setGiftState({ secondPopupClosed: true });
    }

  });

});

function findGift(cart) {
  return cart?.items?.find(i => i.properties?._free_gift === 'Yes');
}

function countPaid(cart) {
  return (cart?.items || []).filter(
    i => i.properties?._free_gift !== 'Yes'
  ).length;
}

/* =========================================
   CHECKOUT LOCK
========================================= */

async function toggleCheckoutLock(cart) {

  const gift = findGift(cart);
  const paidCount = countPaid(cart);

  const checkoutBtn =
    document.querySelector('[name="checkout"]') ||
    document.querySelector('.cart__checkout-button');

  if (!checkoutBtn) return;

  if (gift && paidCount === 0) {

    checkoutBtn.disabled = true;
    checkoutBtn.classList.add('checkout-disabled');
    checkoutBtn.title = "Add a product before checkout";

  } else {
    checkoutBtn.disabled = false;
    checkoutBtn.classList.remove('checkout-disabled');
    checkoutBtn.removeAttribute('title');

  }

}

window.isKlaviyoOpen = function () {
  return !!document.querySelector('[data-testid="POPUP"]');
}

window.waitForKlaviyoToClose = function (callback) {
  const interval = setInterval(() => {
    if (!isKlaviyoOpen()) {
      clearInterval(interval);
      callback();
    }
  }, 300);
}

window.openPopup = function () {
  const dialog = document.querySelector('#free-gift-dialog dialog');

  if (!dialog) {
    console.warn('[FreeGift] Popup not found in DOM');
    return false;
  }

  // 🚫 If Klaviyo is open → wait
  if (isKlaviyoOpen()) {
    waitForKlaviyoToClose(() => {
      dialog.showModal();
    });
    return false;
  }

  dialog.showModal();
  return true;
}

/* =========================================
   TIMED POPUP (BEFORE ADD TO CART)
========================================= */

document.addEventListener('DOMContentLoaded', async () => {

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('drawstring') === 'open') {
    openPopup();
    return;
  }

  const enablePrePopup = window.FREE_GIFT_BEFORE_CART ?? true;
  const delay = window.FREE_GIFT_DELAY ?? 10; // seconds

  //if (!enablePrePopup) return;

  const state = getGiftState();
  // Do not show again if already shown in session
  // if (state.popupShown) return;
  //if (state.firstPopupClosed) return;

  const cart = await getCartFresh();
  // if (!cart) return;

  // Only show timed popup if cart is empty
  // if (cart.item_count !== 0) return;
  setTimeout(() => {
    const stateNow = getGiftState();
    if (stateNow.popupShown) return;
    const show = () => {
      if (openPopup()) {
        setGiftState({ popupShown: true });
      }
    };
    if (isKlaviyoOpen()) {
      waitForKlaviyoToClose(show);
    } else {
      show();
    }
  }, delay * 1000);

});

document.addEventListener('click', function (e) {
  const btn = e.target.closest('.change-gift');
  if (!btn) return;

  e.preventDefault();
  document.querySelector('#free-gift-dialog dialog')?.showModal();
});

/* =========================================
   CORE RULE ENGINE
========================================= */

let settleTimer = null;

document.addEventListener(ThemeEvents.cartUpdate, () => {
  clearTimeout(settleTimer);
  
  settleTimer = setTimeout(async () => {
    console.log("LOAD");
    const cart = await getCartFresh();
    if (!cart?.items) return;

    await toggleCheckoutLock(cart);

    const gift = findGift(cart);
    const paidCount = countPaid(cart);
    const state = getGiftState();

    const freeGiftConfig = window.FREE_GIFT_CONFIG || {
      total: 0,
      available: 0
    };

    /* =====================
       CART EMPTY
    ===================== */
    if (cart.item_count === 0) {
      resetGiftState();
      return;
    }

    /* =====================
       NO AVAILABLE FREE GIFTS
       → DO NOTHING
    ===================== */
    // if (freeGiftConfig.available === 0) {
    //   return;
    // }

    /* =====================
       MANUAL GIFT REMOVAL
    ===================== */
    if (paidCount > 0 && state.added && !gift) {
      setGiftState({ removedManually: true });
    }

    /* =====================
       AUTO ADD SINGLE GIFT
    ===================== */
    async function autoAddSingleGift() {
      const radio = document.querySelector('.gift-radio');
      if (!radio?.value) return;

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: radio.value,
          quantity: 1,
          properties: { _free_gift: 'Yes' }
        })
      });

      setGiftState({
        added: true,
        removedManually: false,
        popupShown: true
      });

      if (openPopup()) {
        setGiftState({ popupShown: true });
      }

      document.dispatchEvent(
        new CartUpdateEvent({ source: 'free-gift-auto-added' })
      );
    }

    /* =====================
       PAID ITEMS EXIST
    ===================== */
    if (!state.paidProductAdded && paidCount > 0) {
      setGiftState({
        paidProductAdded: true
      });

      // If gift already exists → never show popup again
      if (gift) {
        setGiftState({ added: true });
        return;
      }

      if (!state.secondPopupClosed) {
        openPopup();
      }

      // ================================
      // 2. Respect manual removal
      // ================================
      if (state.removedManually) return;

      // ================================
      // 3. FORCE MANUAL MODE
      // ================================
      if (window.ALLOW_FREE_GIFT === false) {
        if (!state.popupShown) {
          openPopup();
          setGiftState({ popupShown: true });
        }
        return;
      }

      // ================================
      // 4. Prevent popup spam
      // ================================
      // Allow second popup when paid product added
      if (state.firstPopupClosed && state.paidProductAdded) {
        // allow popup
      } else if (state.firstPopupClosed) {
        return;
      }

      // Prefer Liquid total, fallback to DOM
      const totalGiftProducts =
        typeof window.FREE_GIFT_TOTAL === 'number'
          ? window.FREE_GIFT_TOTAL
          : document.querySelectorAll('.gift-card .gift-radio').length;

      const variantSelects = document.querySelectorAll('.gift-variant-select');

      // ================================
      // SINGLE PRODUCT
      // ================================
      if (totalGiftProducts === 1) {

        if (variantSelects.length === 1) {
          const select = variantSelects[0];
          const enabledOptions = [...select.options].filter(o => !o.disabled);

          if (enabledOptions.length === 1) {
            await autoAddSingleGift();
            return;
          }

          if (openPopup()) {
            setGiftState({ popupShown: true });
          }
          return;
        }

        await autoAddSingleGift();
        return;
      }

      // ================================
      // MULTIPLE PRODUCTS
      // ================================
      if (totalGiftProducts > 1) {
        if (openPopup()) {
          setGiftState({ popupShown: true });
        }
        return;
      }
    }

    /* =====================
       REMOVE GIFT WHEN NO PAID ITEMS
    ===================== */
    if (paidCount === 0 && gift) {
      const removeGift = window.FREE_GIFT_REMOVE_IF_NO_PAID ?? true;

      if (removeGift && cart.item_count === 1) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: gift.key,
            quantity: 0
          })
        });

        resetGiftState();

        document.dispatchEvent(
          new CartUpdateEvent({ source: 'free-gift-auto-remove' })
        );

      } else {
        // Just disable checkout instead
        await toggleCheckoutLock(cart);

      }

    }
  }, 4000);
});

/* =========================================
   ADD GIFT BUTTON (MANUAL)
========================================= */

document.addEventListener('DOMContentLoaded', () => {
  const addBtn = document.querySelector(
    '#free-gift-dialog .add-to-cart-button'
  );
  if (!addBtn) return;

  addBtn.addEventListener('click', async () => {
    const selected = document.querySelector('.gift-radio:checked');
    if (!selected?.value) {
      alert('Please select a free gift');
      return;
    }

    try {
      addBtn.disabled = true;
      addBtn.classList.add('atc-added');

      // 🔥 Get fresh cart
      const cart = await getCartFresh();
      if (!cart?.items) return;

      const existingGift = findGift(cart);

      /* =========================
         IF GIFT ALREADY EXISTS
         → REMOVE FIRST
      ========================== */
      if (existingGift) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingGift.key,
            quantity: 0
          })
        });
      }

      /* =========================
         ADD NEW SELECTED GIFT
      ========================== */
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selected.value,
          quantity: 1,
          properties: { _free_gift: 'Yes' }
        })
      });

      if (!res.ok) throw new Error('Gift add failed');

      setGiftState({
        added: true,
        removedManually: false,
        popupShown: true
      });

      closePopup();

      document.dispatchEvent(
        new CartUpdateEvent({ source: 'free-gift-replaced' })
      );

    } catch (err) {
      console.error('[FreeGift] Add error:', err);
    } finally {
      addBtn.classList.remove('atc-added');
      addBtn.disabled = false;
    }
  });
});

/* =========================================
   VARIANT RADIO SYNC
========================================= */

document.addEventListener('change', (e) => {
  if (!e.target.classList.contains('gift-variant-select')) return;

  const select = e.target;
  const selectedOption = select.options[select.selectedIndex];
  const card = select.closest('.gift-card');
  const radio = card?.querySelector('.gift-radio');

  if (!radio) return;

  if (selectedOption.disabled) {
    radio.disabled = true;
    radio.checked = false;
  } else {
    radio.disabled = false;
    radio.checked = true;
    radio.value = selectedOption.value;
  }
});

/* =========================================
   MANUAL OPEN BUTTON
========================================= */

document.addEventListener('click', (e) => {
  if (!e.target.matches('#open-free-gift-popup, .ayf-gift')) return;

  setGiftState({
    removedManually: false,
    popupShown: true,
    paidProductAdded: false,
    firstPopupClosed: false,
    secondPopupClosed: false,
  });

  if (isKlaviyoOpen()) {
    waitForKlaviyoToClose(() => openPopup());
  } else {
    openPopup();
  }
});


document.addEventListener('change', async (e) => {
  if (!e.target.matches('.cart-variant-selector')) return;

  const select = e.target;
  const container = select.closest('.cart-items__variants');
  const variantsJSON = container.querySelector('.product-variants');

  if (!variantsJSON) {
    console.error('Variants JSON missing');
    return;
  }

  const variants = JSON.parse(variantsJSON.textContent);
  const selects = container.querySelectorAll('.cart-variant-selector');

  const selectedOptions = [];

  selects.forEach(s => {
    selectedOptions.push(s.options[s.selectedIndex].text.trim());
  });

  const matchedVariant = variants.find(v =>
    v.options.every((opt, i) => opt === selectedOptions[i])
  );

  if (!matchedVariant) {
    console.log('No matching variant');
    return;
  }

  const oldKey = select.dataset.key;

  try {

    /* remove old item */
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: oldKey,
        quantity: 0
      })
    });

    /* small delay prevents race condition */
    await new Promise(resolve => setTimeout(resolve, 200));

    /* add new variant */
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: matchedVariant.id,
          quantity: 1
        }]
      })
    });

    document.dispatchEvent(
        new CartUpdateEvent({ source: 'free-gift-auto-added' })
      );

  } catch (err) {
    console.error('Variant swap failed', err);
  }
});

document.addEventListener('click', function () {
  const klaviyoClose = document.querySelector('.klaviyo-close-form');

  if (klaviyoClose) {
    klaviyoClose.addEventListener('click', () => {
      setTimeout(() => {
        openPopup();
      }, 800);
    });
  }
});



  // document.addEventListener("DOMContentLoaded", function () {
  //   const videos = document.querySelectorAll('.js-autoplay-video');

  //   videos.forEach(video => {
  //     video.play().catch(() => {
  //       // Autoplay blocked (battery saver / iOS)
  //       const playOnTouch = () => {
  //         video.play();
  //         video.removeEventListener('touchstart', playOnTouch);
  //         video.removeEventListener('click', playOnTouch);
  //       };

  //       video.addEventListener('touchstart', playOnTouch);
  //       video.addEventListener('click', playOnTouch);
  //     });
  //   });
  // });

document.addEventListener("DOMContentLoaded", function () {
  const videos = document.querySelectorAll('.js-autoplay-video');
  let hasPlayed = false; // ✅ guard flag

  const playAllVideos = () => {
    if (hasPlayed) return; // 🚫 prevent multiple calls
    hasPlayed = true;

    videos.forEach(video => {
      video.play().catch(() => {});
    });
  };

  // First user interaction anywhere
  document.addEventListener('touchstart', playAllVideos, { once: true });
  document.addEventListener('click', playAllVideos, { once: true });
});


// Slideshow change events
document.addEventListener('DOMContentLoaded', () => {
  const slideshow = document.querySelector('slideshow-component');

  if (!slideshow) return;

  const targetContainer = document.querySelector('#slide_wear_size');

  // Allowed sizes
  const validSizes = ["XS", "S", "M", "L", "XL", "XXL"];

  slideshow.addEventListener('slideshow:select', (event) => {
    const { index, slide } = event.detail;

    document.body.setAttribute('data-active-slide', index);

    if (!slide || !targetContainer) return;

    const mediaEl = slide.querySelector('.product-media[data-image-attribute]');

    if (mediaEl) {
      let value = mediaEl.getAttribute('data-image-attribute');

      if (value) {
        value = value.trim().toUpperCase(); // normalize

        if (validSizes.includes(value)) {
          targetContainer.innerHTML = `<div class="centered waring-size size_indicator font-12">${value}</div>`;
          targetContainer.classList.add('active');
          return;
        }
      }
    }

    // If not valid → clear
    targetContainer.innerHTML = '';
    targetContainer.classList.remove('active');
  });

  // Gift popup
  const body = document.body;
  const earlyDialog = document.querySelector('#early-access-success dialog');
  const dialogPass = document.querySelector('#early-access-password dialog');
  const passwordInput = document.querySelector('#access-password');
  const submitBtn = document.querySelector('#submit-password');
  const errorMsg = document.querySelector('#password-error');
  const closeBtn = document.querySelector('#close-password');

  const params = new URLSearchParams(window.location.search);
  const type = params.get('type');

  const CORRECT_PASSWORD = "EARLY2026";
  // if (type === 'early-access') {
  //   earlyDialog.showModal();
  // }

  // Open popup if query matches
  if (type === 'early-access-password' && dialogPass) {
    // dialogPass.showModal();
    // body.classList.add('early-access-active');
  }

  // Submit password
  // if (submitBtn) {
  //   submitBtn.addEventListener('click', () => {
  //     const entered = passwordInput.value.trim();

  //     if (entered === CORRECT_PASSWORD) {
  //       dialogPass.close();
  //       body.classList.remove('early-access-active');
  //       errorMsg.style.display = 'none';

  //       // ✅ Remove query param from URL
  //       const url = new URL(window.location);
  //       url.searchParams.delete('type');
  //       window.history.replaceState({}, document.title, url.pathname + url.search);
        
  //     } else {
  //       errorMsg.style.display = 'block';
  //     }
  //   });
  // }

  // Close button (optional)
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      dialogPass.close();
      body.classList.remove('early-access-active');
    });
  }

  // Also handle native dialog close (ESC or backdrop click)
  if (dialogPass) {
    dialogPass.addEventListener('close', () => {
      body.classList.remove('early-access-active');
    });
  }

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.mother-gift-btn');
    if (!btn) return;

    e.preventDefault();

    const card = btn.closest('.gift-card');
    const radio = card?.querySelector('.gift-radio');
    const select = card?.querySelector('.gift-variant-select');

    let variantId = radio?.value;

    if (select) {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption && !selectedOption.disabled) {
        variantId = selectedOption.value;
      }
    }

    if (!variantId) {
      alert('Please select a variant');
      return;
    }

    try {
      btn.disabled = true;
      btn.classList.add('atc-added');

      const cart = await getCartFresh();
      if (!cart?.items) return;

      const existingGift = findGift(cart);

      if (existingGift) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingGift.key,
            quantity: 0
          })
        });
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _free_gift: 'Yes' }
        })
      });

      if (!res.ok) throw new Error('Gift add failed');

      setGiftState({
        added: true,
        removedManually: false,
        popupShown: true
      });

      document.dispatchEvent(
        new CartUpdateEvent({ source: 'free-gift-added-from-card' })
      );

    } catch (err) {
      console.error('[FreeGift] Add error:', err);
    } finally {
      btn.classList.remove('atc-added');
      btn.disabled = false;
    }
  });
});



// Header Swatch
document.addEventListener("DOMContentLoaded", function () {
  const slider = document.querySelector(".color-slider");
  const nextBtn = document.querySelector(".color-nav.next");
  const prevBtn = document.querySelector(".color-nav.prev");
  const sliderWraper = document.querySelector(".color-slider-wrapper");

  if (!slider || !nextBtn || !prevBtn) return;

  const scrollAmount = 72;

  function updateButtons() {
    const maxScrollLeft = slider.scrollWidth - slider.clientWidth;

    // LEFT
    if (slider.scrollLeft <= 2) {
      prevBtn.classList.add("hidden");
      sliderWraper.classList.add("hide-prev");
    } else {
      prevBtn.classList.remove("hidden");
      sliderWraper.classList.remove("hide-prev");
    }

    // RIGHT
    if (slider.scrollLeft >= maxScrollLeft - 2) {
      nextBtn.classList.add("hidden");
      sliderWraper.classList.add("hide-next");
    } else {
      nextBtn.classList.remove("hidden");
     sliderWraper.classList.remove("hide-next");
    }
  }

  function checkOverflow() {
    const hasOverflow = slider.scrollWidth > slider.clientWidth + 2;

    if (hasOverflow) {
      nextBtn.classList.remove("hidden");
      slider.classList.remove("hide-next");
    } else {
      nextBtn.classList.add("hidden");
      prevBtn.classList.add("hidden");
      slider.classList.remove("hide-prev");
      slider.classList.remove("hide-next");
    }
  }

  function init() {
    checkOverflow();
    updateButtons();
  }

  nextBtn.addEventListener("click", () => {
    slider.scrollBy({ left: scrollAmount, behavior: "smooth" });
  });

  prevBtn.addEventListener("click", () => {
    slider.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  });

  slider.addEventListener("scroll", updateButtons);

  // ✅ ONLY ONE reliable init trigger
  window.addEventListener("load", () => {
    init();
  });

  // ✅ Resize fix
  window.addEventListener("resize", init);

  // ✅ BEST FIX (handles image load + layout changes perfectly)
  // const observer = new ResizeObserver(() => {
  //   init();
  // });

  // observer.observe(slider);

}); 
